import { FC, useContext, useState } from "react";

import DealBuilderContext from "~/contexts/DealBuilderContext";
import OrderContext from "~/contexts/OrderContext";
import MenuContext from "~/contexts/MenuContext";
import TaxContext from "~/contexts/TaxContext";
import ThemeContext from "~/contexts/ThemeContext";
import { CartLine } from "~/entities/orderEntities";
import { MenuTabItem } from "~/entities/menuEntities";
import { choiceRequiredForProduct, generateLine } from "~/helpers/cartHelpers";
import { withIndefiniteArticle } from "~/helpers/dealHelpers";
import ProductModal from "~/modules/order/containers/ProductModal/ProductModal";
import CloseIcon from "~/icons/CloseIcon";

import styles from "./DealNudge.module.css";

// "Add a drink to make it a Burger Combo": shown when loose items are one short of a deal. The customer picks which
// item completes it. Amounts are estimated from menu prices; the cart shows the server's once added.
const DealNudge: FC = () => {
  const { partialDeal, dismissPartialDeal } = useContext(DealBuilderContext);
  const { addOrderLines, orderSource, readonlyCart } = useContext(OrderContext);
  const { menu } = useContext(MenuContext);
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);
  const [optionsLine, setOptionsLine] = useState<CartLine | null>(null);

  if (!partialDeal || !menu) {
    return null;
  }
  const { deal, slot, increase, saving, savingVaries, options } = partialDeal;

  const add = (menuItem: MenuTabItem) => {
    const line = generateLine(menuItem, menu.code);
    if (choiceRequiredForProduct(menuItem.productData, orderSource)) {
      setOptionsLine(line);
    } else {
      addOrderLines([line]);
    }
  };

  const change =
    increase > 0
      ? `Your order goes up by just ${formatAmount(increase)}`
      : increase < 0
        ? `Your order goes down by ${formatAmount(-increase)}`
        : "Your order stays the same price";

  return (
    <section
      className={styles.nudge}
      style={{ borderColor: template.global.button.primaryButtonColour }}
      aria-label={`Make it ${withIndefiniteArticle(deal.name)}`}
    >
      <ProductModal
        isInsert
        initLine={optionsLine}
        onClose={() => setOptionsLine(null)}
      />
      <div className={styles.header}>
        <b className={styles.title}>
          Add {withIndefiniteArticle(slot.label.toLowerCase())} to make it{" "}
          {withIndefiniteArticle(deal.name)}
        </b>
        <button
          type="button"
          className={styles.dismiss}
          aria-label="Dismiss"
          onClick={dismissPartialDeal}
        >
          <CloseIcon />
        </button>
      </div>
      <p className={styles.body}>
        {change} and you save {savingVaries ? "up to " : ""}
        {formatAmount(saving)}.
      </p>
      <div className={styles.chips}>
        {options.map(({ menuItem }) => (
          <button
            key={menuItem.productCode}
            type="button"
            className={styles.chip}
            disabled={readonlyCart}
            onClick={() => add(menuItem)}
          >
            {menuItem.productData.name}
          </button>
        ))}
      </div>
    </section>
  );
};

export default DealNudge;
